import React, { useState, useEffect } from "react";
import TextInput from "../library/TextInput";
import PaginatedTable from "../library/Pagination";
import API_BASE_URL from "../../config/api";

const WorkExperience = ({ userId }) => {
  const [rows, setRows] = useState([]);
  const [notification, setNotification] = useState(null);

  const fetchData = () => {
    fetch(`${API_BASE_URL}/api/WorkExperience/employee/${userId}`)
      .then((res) => res.json())
      .then(setRows)
      .catch(console.error);
  };
  useEffect(fetchData, [userId]);

  const handleChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;
    setRows(updated);
  };
  const validateDates = (start, end, isStartRequired = true) => {
    const errors = {};
    const today = new Date().toISOString().split("T")[0];

    // Check required start date
    if (!start && isStartRequired) {
      errors.startDate = "Start date is required.";
    } else if (start > today) {
      errors.startDate = "Start date must be today or in the past.";
    }

    // End date checks (if provided)
    if (end) {
      if (start && end < start) {
        errors.endDate = "End date cannot be before start date.";
      }
    }

    return errors;
  };

  const handleEditToggle = async (index) => {
    const updated = [...rows];
    const row = updated[index];

    // Toggle to edit mode if not editing
    if (!row.isEditing) {
      updated[index].isEditing = true;
      setRows(updated);
      return;
    }
    if (!row.company?.trim() || !row.jobTitle?.trim() || !row.startDate || !row.endDate) {
      setNotification({
        type: "error",
        message: "Please fill in all fields before saving.",
      });
      return;
    }

    // Validate
    const errors = validateDates(row.startDate?.split("T")[0], row.endDate?.split("T")[0]);
    updated[index].errors = errors;

    if (Object.keys(errors).length > 0) {
      const firstError =
        errors.startDate || errors.endDate || "Please check the form.";
      setNotification({
        type: "error",
        message: firstError,
      });
      return;
    }
    handleCloseNotification();

    // Map frontend fields to backend fields
    const payload = {
      id: row.id || 0,
      company: row.company || "",
      jobTitle: row.jobTitle || "",
      startDate: row.startDate || "",
      endDate: row.endDate || "",
      employeeId: userId
    };

    const method = row.id ? "PUT" : "POST";
    const url = row.id
      ? `${API_BASE_URL}/api/WorkExperience/${row.id}`
      : `${API_BASE_URL}/api/WorkExperience`;

    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error:", response.status, errorText);
      } else {
        fetchData(); // Refresh rows from DB
        updated[index].isEditing = false; // Exit edit mode
        setRows(updated);
      }
    } catch (err) {
      console.error("Request failed:", err);
    }
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const handleDelete = async (index) => {
    const row = rows[index];
    if (row.id)
      await fetch(`${API_BASE_URL}/api/WorkExperience/${row.id}`, {
        method: "DELETE",
      });
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        company: "",
        jobTitle: "",
        startDate: "",
        endDate: "",
        isEditing: true,
      },
    ]);
  };

  return (
    <div className="p-4">
      <h2 className="text-[#111518] text-[22px] font-bold leading-tight tracking-[-0.015em] pb-3">
        Work Experience
      </h2>

      {notification && (
        <div className={`mb-4 px-4 py-3 rounded flex justify-between items-start ${notification.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
          }`}>
          <span>{notification.message}</span>
          <button onClick={handleCloseNotification} className="ml-4 font-bold text-lg leading-none">×</button>
        </div>
      )}


      <div className="px-4 py-3">
        <div className="flex overflow-hidden rounded-xl border border-[#dde1e3] bg-white">
          <PaginatedTable
            columns={[
              "Company",
              "Job Title",
              "Start Date",
              "End Date",
              "Action",
            ]}
            data={rows}
            pageSize={10}
            showPagination={false}
            renderRow={(row, index) => (
              <tr key={row.id || index} className="border-t border-t-[#dde1e3]">
                <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal">
                  <TextInput
                    value={row.company}
                    className={row.isEditing ? "input-editing" : "input-readonly"}
                    disabled={!row.isEditing}
                    onChange={(e) => handleChange(index, "company", e.target.value)}
                  />
                </td>
                <td className="h-[72px] px-4 py-2 text-[#121516] text-sm font-normal leading-normal">
                  <TextInput
                    value={row.jobTitle}
                    className={row.isEditing ? "input-editing" : "input-readonly"}
                    disabled={!row.isEditing}
                    onChange={(e) => handleChange(index, "jobTitle", e.target.value)}
                  />
                </td>
                <td className="h-[72px] px-4 py-2 text-[#6a7881] text-sm font-normal leading-normal">
                  <TextInput
                    type="date"
                    value={row.startDate?.split("T")[0]}
                    className={row.isEditing ? "input-editing" : "input-readonly"}
                    disabled={!row.isEditing}
                    onChange={(e) => handleChange(index, "startDate", e.target.value)}
                  />
                </td>

                <td className="h-[72px] px-4 py-2 text-[#6a7881] text-sm font-bold leading-normal tracking-[0.015em]">
                  <TextInput
                    type="date"
                    value={row.endDate?.split("T")[0]}
                    className={row.isEditing ? "input-editing" : "input-readonly"}
                    disabled={!row.isEditing}
                    onChange={(e) => handleChange(index, "endDate", e.target.value)}
                  />
                </td>

                <td className="h-[72px] px-4 py-2 text-sm font-normal leading-normal">
                  <div className="flex gap-2">
                    <button
                      className="text-blue-600"
                      onClick={() => handleEditToggle(index)}
                    >
                      {row.isEditing ? "Save" : "Edit"}
                    </button>
                    <button
                      className="text-red-600"
                      onClick={() => handleDelete(index)}
                    >
                      Delete
                    </button>
                  </div>
                </td>

              </tr>
            )}
          />

        </div>
      </div>

      <div className="col-span-5 flex justify-end">
        <button
          className="btn-black"
          onClick={handleAddRow}
        >
          + Add Row
        </button>
      </div>

    </div>
  );
};

export default WorkExperience;
